import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Code2, Zap, Package, CheckCircle2 } from 'lucide-react';
import { useWorkbenchToast } from '../shared/toast';

const SDK_EXAMPLES = {
  install: `npm install @getclearance/react-sdk`,
  react: `import { VerificationFlow } from '@getclearance/react-sdk';
import '@getclearance/react-sdk/styles.css';

function VerifyPage({ sessionToken }) {
  return (
    <VerificationFlow
      sessionToken={sessionToken}
      onComplete={(result) => {
        if (result.outcome === 'approved') {
          console.log('Verified!', result);
        }
      }}
    />
  );
}`,
  backend: `// Your backend (Node.js example)
const response = await fetch('https://api.getclearance.dev/v1/verifications', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.GETCLEARANCE_API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    customer_id: user.id,
    redirect_url: 'https://yourapp.com/verify-complete'
  })
});

const { sdk_token } = await response.json();`,
  nextjs: `// app/verify/page.tsx
'use client';
import dynamic from 'next/dynamic';
import '@getclearance/react-sdk/styles.css';

const VerificationFlow = dynamic(
  () => import('@getclearance/react-sdk').then(mod => mod.VerificationFlow),
  { ssr: false }
);

export default function VerifyPage() {
  const [token, setToken] = useState(null);

  useEffect(() => {
    fetch('/api/create-verification')
      .then(r => r.json())
      .then(data => setToken(data.sdk_token));
  }, []);

  if (!token) return <div>Loading...</div>;

  return (
    <VerificationFlow
      sessionToken={token}
      onComplete={(result) => {
        window.location.href = '/dashboard';
      }}
    />
  );
}`,
};

const FEATURES = [
  { title: 'Document Capture', desc: 'Passport, license, ID card' },
  { title: 'Selfie Verification', desc: 'Face matching with ID photo' },
  { title: 'Liveness Detection', desc: 'Prevents photos and deepfakes' },
  { title: 'White-label Theming', desc: 'Match your brand colors' },
];

export default function QuickstartTab() {
  const [activeTab, setActiveTab] = useState('react');
  const toast = useWorkbenchToast();

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    toast.copied();
  };

  return (
    <div className="quickstart-container">
      <div className="quickstart-header">
        <h2>Quickstart</h2>
        <p>Add identity verification to your React app in 5 minutes</p>
      </div>

      {/* SDK Hero */}
      <div className="workbench-card sdk-hero">
        <div className="sdk-hero-header">
          <div className="sdk-icon">
            <Package size={24} />
          </div>
          <div>
            <h3>@getclearance/react-sdk</h3>
            <p>Official React SDK for GetClearance</p>
          </div>
        </div>

        <div className="sdk-features">
          {FEATURES.map(f => (
            <span key={f.title} className="workbench-badge workbench-badge-success">
              <CheckCircle2 size={12} />
              {f.title}
            </span>
          ))}
        </div>

        <div className="sdk-install">
          <pre className="workbench-code-block">{SDK_EXAMPLES.install}</pre>
          <button className="embed-copy-btn" onClick={() => handleCopy(SDK_EXAMPLES.install)}>
            <Copy size={14} />
            Copy
          </button>
        </div>
      </div>

      {/* Step 1 */}
      <div className="workbench-card quickstart-step">
        <div className="step-header">
          <span className="step-badge">1</span>
          <h3>Get a session token (backend)</h3>
        </div>
        <p>Create a verification session from your backend to get a secure token for the SDK.</p>
        <div className="code-with-copy">
          <pre className="workbench-code-block">{SDK_EXAMPLES.backend}</pre>
          <button className="embed-copy-btn" onClick={() => handleCopy(SDK_EXAMPLES.backend)}>
            <Copy size={14} /> Copy
          </button>
        </div>
        <p className="step-note">
          Get your API key from the <Link to="/dev-workbench?tab=keys" className="workbench-link">API Keys tab</Link>.
        </p>
      </div>

      {/* Step 2 */}
      <div className="workbench-card quickstart-step">
        <div className="step-header">
          <span className="step-badge">2</span>
          <h3>Add the component (frontend)</h3>
        </div>

        <div className="code-tabs">
          <button
            className={`code-tab ${activeTab === 'react' ? 'active' : ''}`}
            onClick={() => setActiveTab('react')}
          >
            <Code2 size={14} />
            React
          </button>
          <button
            className={`code-tab ${activeTab === 'nextjs' ? 'active' : ''}`}
            onClick={() => setActiveTab('nextjs')}
          >
            <Zap size={14} />
            Next.js
          </button>
        </div>

        <div className="code-with-copy">
          <pre className="workbench-code-block">
            {activeTab === 'react' ? SDK_EXAMPLES.react : SDK_EXAMPLES.nextjs}
          </pre>
          <button className="embed-copy-btn" onClick={() => handleCopy(activeTab === 'react' ? SDK_EXAMPLES.react : SDK_EXAMPLES.nextjs)}>
            <Copy size={14} /> Copy
          </button>
        </div>

        <p className="step-note">
          That's it! The SDK handles the entire verification flow.
        </p>
      </div>

      {/* Go Live Checklist */}
      <div className="workbench-card quickstart-step">
        <div className="step-header">
          <h3>Go live checklist</h3>
        </div>
        <ul className="checklist">
          <li>
            <span className="checklist-num">1</span>
            Get your API key — <Link to="/dev-workbench?tab=keys" className="workbench-link">API Keys tab</Link>
          </li>
          <li>
            <span className="checklist-num">2</span>
            Add a payment method — <Link to="/billing" className="workbench-link">Billing page</Link>
          </li>
          <li>
            <span className="checklist-num">3</span>
            Configure your verification flow in the dashboard
          </li>
          <li>
            <span className="checklist-num">4</span>
            Test with a real device and camera
          </li>
        </ul>
      </div>
    </div>
  );
}
